// frontend/src/pages/PrivacyPolicy.js
import React from 'react';
import { Box, Container, Typography, Link, List, ListItem, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { Helmet } from 'react-helmet-async';

const H5 = (p) => <Typography variant="h5" fontWeight={700} gutterBottom {...p} />;
const P = (p) => <Typography sx={{ mb: 2 }} {...p} />;

export default function PrivacyPolicy() {
  const updated = 'April 17, 2026';

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Blackmount</title>
        <meta name="description" content="How Blackmount.ai collects, uses, and protects information across our website, apps, and browser extension." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://app.blackmount.ai/privacy-policy" />
      </Helmet>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h3" fontWeight={800} gutterBottom>Privacy Policy</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Last updated {updated}</Typography>

        <P>
          This Privacy Notice for <strong>Blackmount.ai Inc.</strong> ("<strong>Blackmount</strong>," "we," "us," "our") explains how we collect, use,
          disclose, and protect personal information when you use our website (<Link href="https://www.blackmount.ai" target="_blank" rel="noopener">blackmount.ai</Link>),
          web app (<Link href="https://app.blackmount.ai" target="_blank" rel="noopener">app.blackmount.ai</Link>), browser extension, and mobile/desktop apps
          (together, the "Services"). If you do not agree, please do not use the Services. Contact us at <Link href="mailto:support@blackmount.ai">support@blackmount.ai</Link>.
        </P>

        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, background: 'background.paper', mb: 4 }}>
          <Typography fontWeight={700} gutterBottom>Summary of Key Points</Typography>
          <List dense sx={{ listStyle: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item' }}>
              By default we save <em>URL, title, favicon, project/tags</em>. Optional features such as <em>Capture Content</em> and <em>Quick Voice</em>
              process additional data only with your consent and can be disabled at any time.
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              AI processing (if enabled) generates summaries/tags/embeddings. We configure vendors not to train on your data where controls exist and require contractual limits.
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              We do not sell or "share" your personal information for cross-context behavioral advertising.
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              You have rights to access, delete, correct, export, and appeal decisions. See <Link href="#your-rights">Your Rights</Link>.
            </ListItem>
          </List>
        </Box>

        <H5 id="who">1) Who is responsible</H5>
        <P><strong>Controller:</strong> Blackmount.ai Inc., PO Box 641, Palo Alto, CA 94301, USA. Email: <Link href="mailto:support@blackmount.ai">support@blackmount.ai</Link>.</P>
        <P><strong>EEA/UK users:</strong> We rely on appropriate safeguards (e.g., Standard Contractual Clauses) for transfers to the U.S. If we appoint an EU/UK representative or DPO, we will update this page.</P>

        <H5 id="what-we-collect">2) Information we collect</H5>
        <P>Information you provide directly and information generated by your use of the Services:</P>
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: 190, fontWeight: 600 }}>Account & Contact</TableCell>
              <TableCell>Name, email, password (hashed), organization (if any), support messages.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Product Data</TableCell>
              <TableCell>
                <strong>Bookmarks & Sessions (default):</strong> title, URL, favicon, project/tags, timestamps.<br/>
                <strong>Optional — Capture Content:</strong> with your consent, we may extract limited on-page text (or generate an AI summary/embeddings).<br/>
                <strong>Optional — Quick Voice:</strong> audio you record, transcripts you create.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Device & Diagnostics</TableCell>
              <TableCell>Device/OS/browser, crash/performance logs, basic analytics, cookies/local storage (auth, preferences, feature flags).</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Legal Consent Records</TableCell>
              <TableCell>IP address and user-agent string when you accept our Terms of Service or Privacy Policy (required for legal compliance and GDPR consent verification).</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Payments</TableCell>
              <TableCell>Processed by PCI-compliant providers (e.g., Stripe). We do not store full card numbers.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <P><strong>Sensitive information:</strong> We do not intend to collect sensitive categories. Do not include secrets, credentials, or special category data in notes/recordings. If you enable Capture Content on pages that contain such data, you are responsible for ensuring you have a lawful basis to process it.</P>

        <H5 id="how-we-use">3) How we use information</H5>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>Provide and improve Services, personalize projects/search.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Generate summaries/tags/embeddings (only if AI features are enabled).</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Communicate about updates, security, and support.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Detect, prevent, and respond to fraud, abuse, or security incidents.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Comply with law and enforce terms.</ListItem>
        </List>
        <P><strong>Legal bases (EEA/UK):</strong> consent (AI features, microphone), contract (provide the service), legitimate interests (product safety, improvement, minimal analytics), compliance with legal obligations.</P>

        <H5 id="ai-processing">4) AI processing & "Capture Content"</H5>
        <P>AI features are <strong>opt-in</strong>. When enabled, we minimize inputs (title, selected text, first portion of content, deduplicated) and apply pattern-based redaction (e.g., obvious credentials, card numbers) before sending to model providers strictly to deliver the feature.</P>
        <P>
          We use AI and machine learning models strictly to deliver features such as transcription, summarization, embeddings, and personalized recommendations.
          <strong> We do not use user data to train foundation models.</strong> Your data is processed only to provide and improve your individual experience.
        </P>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Vendors & training:</strong> We configure available controls to disable training on your data and require contractual limits. A live list of subprocessors is maintained at <Link href="/legal/subprocessors">/legal/subprocessors</Link> (or available on request).
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Outputs:</strong> summaries, tags, and embeddings are stored with your bookmark/session and deleted when you delete that item or disable AI features.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>No automated decisions with legal or similarly significant effects.</strong>
          </ListItem>
        </List>

        <H5 id="third-party-ai">4b) AI Features and Third-Party Processing</H5>
        <P>
          Blackmount offers optional AI-powered features including text summarization, title generation, and content tagging. These features are <strong>off by default</strong> and are only activated when you explicitly enable them.
        </P>
        <P><strong>Third-Party AI Processor</strong></P>
        <P>
          When AI features are enabled, certain content is sent to <strong>Groq, Inc.</strong> ("<strong>Groq</strong>"), located at <Link href="https://groq.com" target="_blank" rel="noopener">groq.com</Link>, for processing. Groq acts as a data processor on our behalf.
        </P>
        <P><strong>What data is sent to Groq:</strong></P>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>Text content of voice note transcripts (for summarization)</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Text content of bookmarks including title, description, and notes (for summarization)</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Session metadata including titles (for AI-generated title suggestions)</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Content tags and categories (for AI-generated tagging)</ListItem>
        </List>
        <P><strong>What data is NOT sent to Groq:</strong></P>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>Voice audio recordings (transcription is performed on-device using Apple's Speech framework on iOS)</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Email addresses or account credentials</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Payment information</ListItem>
          <ListItem sx={{ display: 'list-item' }}>AI chat conversation bodies (the AI Chats feature on iOS is read-only and does not transmit chat content to Groq)</ListItem>
        </List>
        <P><strong>How we obtain your consent:</strong></P>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>On iOS, the first time you use an AI-powered feature, the app presents a consent screen that names Groq, Inc. as the data recipient, describes what data will be sent, and requires your explicit approval before any data is transmitted.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>In Settings, each AI feature toggle clearly identifies Groq, Inc. as the processor.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>You can disable any AI feature at any time in Settings, which immediately stops future data transmission to Groq for that feature.</ListItem>
        </List>
        <P>
          <strong>Data retention by Groq:</strong> Groq does not retain inference input data by default, except as may be required for service reliability and abuse monitoring. For details, see <Link href="https://groq.com/privacy-policy/" target="_blank" rel="noopener">Groq's privacy practices</Link>.
        </P>
        <P>
          <strong>Data protection:</strong> Groq provides the same or equal protection for personal data as described in this privacy policy. Data is transmitted to Groq over encrypted connections (TLS). Groq processes data in the United States. For users in the European Economic Area or United Kingdom, this constitutes a transfer of personal data to a third country, covered by Standard Contractual Clauses where applicable.
        </P>
        <P><strong>Your controls:</strong></P>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>Disable AI features anytime in <Link href="/settings">Settings</Link></ListItem>
          <ListItem sx={{ display: 'list-item' }}>Request deletion of AI-generated content (summaries, titles, tags) by contacting <Link href="mailto:support@blackmount.ai">support@blackmount.ai</Link></ListItem>
          <ListItem sx={{ display: 'list-item' }}>All AI features are off by default — no data is sent to Groq unless you explicitly opt in</ListItem>
        </List>

        <H5 id="do-not-sell">4c) Do Not Sell or Share</H5>
        <P>
          Blackmount does not sell or share your personal information as defined under the California Consumer Privacy Act (CCPA/CPRA). Groq acts solely as a data processor and does not use your data for its own purposes.
        </P>

        <H5 id="extension">5) Browser extension specifics</H5>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Default "Save as Session":</strong> collects active tab URL, title, favicon, and your chosen project/tags. We request the minimum Chrome permissions and access page content only when needed and with your action.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Optional "Capture Content":</strong> the extension first checks eligibility with the web app (plan/limits). If approved and you confirm, we capture a limited text snippet and/or generate an AI summary/embeddings. We do not collect page content otherwise.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Form fields:</strong> the extension is designed not to read form fields such as passwords or payment details. If you believe sensitive fields were captured, contact us and disable Capture Content.
          </ListItem>
        </List>

        {/* NEW: Permissions rationale (Chrome Web Store compliance) */}
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: 220, fontWeight: 600 }}>Permission</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Why we need it</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>activeTab</TableCell>
              <TableCell>Read the current tab's URL/title when you click "Save".</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>tabs</TableCell>
              <TableCell>Enumerate open tabs to save/restore sessions on your command.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>storage</TableCell>
              <TableCell>Store extension settings and short-lived cache to improve UX.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>scripting (on click)</TableCell>
              <TableCell>Inject a lightweight content script only when you opt into "Capture Content".</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <P><strong>No background collection:</strong> We do <em>not</em> continuously monitor or harvest your browsing history. Data flows only when you take an action (e.g., Save/Restore, Capture Content).</P>
        <P><strong>Chrome Web Store Restricted Use:</strong> We do not use or transfer personal and sensitive user data for purposes unrelated to the extension's core functionality, we don't sell data, and we don't share it for cross-context behavioral advertising.</P>

        <H5 id="ai-chat-capture">5b) AI Chat Capture (supported AI chat platforms)</H5>
        <P>
          When you visit a conversation page on a supported AI chat platform — currently <strong>ChatGPT, Claude, Gemini, Grok, Meta AI, DeepSeek, and Perplexity</strong> —
          the Blackmount browser extension may capture conversation data from that page so you can view, search, and sync your own conversations across devices.
        </P>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Metadata:</strong> conversation title, URL, message count, first user message, timestamp, and platform name. Metadata syncs automatically for signed-in users when the extension captures a conversation.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Conversation content:</strong> the full text of user messages and AI responses. Content syncs by default for signed-in users. You can opt out at any time from the extension settings or from <Link href="/settings">Settings</Link> in the web app, which immediately stops future body uploads.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Your control:</strong> you can disable content sync at any time. Opting out permanently deletes all previously uploaded conversation content from your account on our servers. You can also disable AI chat capture entirely from the extension settings.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>How we use it:</strong> only to let you view, search, and sync your own conversations. We do not sell this data, share it for advertising, or use it to train any AI or machine-learning models. Encrypted in transit (TLS 1.2+) and at rest.
          </ListItem>
        </List>

        <H5 id="sharing">6) How we disclose information</H5>
        <P>We do <strong>not</strong> sell or "share" personal information for cross-context behavioral advertising. We disclose data only to:</P>
        <List dense sx={{ listStyle: 'disc', pl: 3 }}>
          <ListItem sx={{ display: 'list-item' }}>Cloud hosting, databases, search/embedding, observability, email delivery, and payment processors.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Trusted AI service providers (OpenAI, Groq, Grok, Gemini) used to power select features, under strict confidentiality and contractual safeguards.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Authorized enterprise administrators (for organization accounts).</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Regulators/law enforcement when legally required.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Successors in a business transfer with notice and your choices.</ListItem>
        </List>
        <P>Our current subprocessors and their locations are listed at <Link href="/legal/subprocessors">/legal/subprocessors</Link>. We will give advance notice of material changes where required.</P>

        <H5 id="security">7) Security</H5>
        <List dense sx={{ listStyle: 'disc', pl: 3 }}>
          <ListItem sx={{ display: 'list-item' }}>Encryption in transit (TLS 1.2+) and at rest.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Role-based access, least privilege, logging, and key/secret rotation.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Vulnerability management and incident response procedures.</ListItem>
        </List>
        <P>No security program can guarantee 100% protection. If we learn of a breach affecting your data, we will notify you and regulators as required by law.</P>

        <H5 id="retention">8) Data retention</H5>
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: 220, fontWeight: 600 }}>Account</TableCell>
              <TableCell>For the life of the account and for up to 30 days after closure (backup cycling), unless legal obligations require longer.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Bookmarks & Sessions (default)</TableCell>
              <TableCell>Until you delete them or close your account.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>AI summaries/embeddings (optional)</TableCell>
              <TableCell>Deleted when the related bookmark/session is deleted or when you disable AI features.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Voice recordings/transcripts (optional)</TableCell>
              <TableCell>User-controlled; default retention can be set in <Link href="/settings">Settings</Link>.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Billing & tax records</TableCell>
              <TableCell>Per applicable law (typically 7 years).</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <P>
          If you do not log in for 12 months, we may deactivate your account and delete your stored data. You may also request permanent deletion of your account and associated data at any time.
        </P>

        <H5 id="your-rights">9) Your privacy rights & choices</H5>
        <P>Depending on your location, you may have the right to access, delete, correct, restrict, object, opt out of sale/share/targeted advertising, receive a portable copy, and withdraw consent.</P>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>Use <Link href="/settings">Settings</Link> to export/delete data, manage AI features, and set retention.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Or email <Link href="mailto:support@blackmount.ai">support@blackmount.ai</Link>. We will verify requests (e.g., email confirmation, session checks).</ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Appeals:</strong> If we deny your request, you may appeal by replying "Appeal" to our decision email or emailing
            <Link href="mailto:support@blackmount.ai"> support@blackmount.ai</Link> with subject "Appeal." If unresolved, you may contact your regulator.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Authorized agents (CA):</strong> You may designate an agent; we may require proof of authorization and your verification.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <strong>Non-discrimination:</strong> We will not discriminate for exercising privacy rights. Some features may require data processing to function.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            You may permanently delete your account and all associated data at any time. Once deleted, data cannot be recovered.
          </ListItem>
        </List>
        <P><strong>Global Privacy Control (GPC):</strong> We do not sell/share personal information. If that changes, we will honor GPC signals as opt-out requests.</P>
        <P><strong>Do Not Track:</strong> We do not respond to DNT signals due to industry standards variability.</P>

        <H5 id="international">10) International transfers</H5>
        <P>
          We may transfer information to the United States and other countries where our cloud providers operate (AWS, GCP).
          For EU/UK users, such transfers rely on Standard Contractual Clauses (SCCs) or equivalent safeguards consistent with GDPR.
          You may request details of these safeguards at <Link href="mailto:support@blackmount.ai">support@blackmount.ai</Link>.
        </P>

        <H5 id="voice-law">11) Voice & local recording laws</H5>
        <P>Quick Voice requires microphone permission. Recording laws vary by jurisdiction (e.g., one-party vs. two-party consent). You are responsible for complying with applicable laws and obtaining consent from participants before recording.</P>

        <H5 id="children">12) Children's privacy</H5>
        <P>The Services are not directed to children under 13 (or older where required). We do not knowingly collect children's personal information. If you believe a child has provided personal information, contact us to request deletion. We may take steps to remove the account.</P>

        <H5 id="paid">13) Paid features & subscriptions</H5>
        <P>
          We offer both free and paid subscription tiers (such as Pro plans with premium storage, extended voice recording, and enhanced AI features).
          Payments are processed securely through Stripe, a PCI-compliant payment processor. We do not store full credit card numbers.
          You may cancel your subscription at any time through <Link href="/settings">Settings</Link>.
        </P>

        <H5 id="changes">14) Changes to this notice</H5>
        <P>We will post updates here and update the "Last updated" date. For material changes, we will provide additional notice (e.g., email or in-app) and obtain consent where required.</P>

        <H5 id="contact">15) Contact us</H5>
        <P>
          Email: <Link href="mailto:support@blackmount.ai">support@blackmount.ai</Link><br/>
          Mail: Blackmount.ai Inc., PO Box 641, Palo Alto, CA 94301, United States
        </P>

        {/* Optional helper: brief Chrome data disclosure mapping */}
        <Box sx={{ mt: 4, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography fontWeight={700} gutterBottom>Data Disclosure (Chrome Web Store summary)</Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: 260, fontWeight: 600 }}>Data category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Collected</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Used for</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Shared</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Account identifiers (email)</TableCell>
                <TableCell>Yes</TableCell>
                <TableCell>Authentication/support</TableCell>
                <TableCell>No</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Web content you save (URL/title/favicon; optional snippet)</TableCell>
                <TableCell>Yes (user-initiated)</TableCell>
                <TableCell>Core features (bookmarks/sessions, optional AI)</TableCell>
                <TableCell>No</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Diagnostics & crash data</TableCell>
                <TableCell>Yes</TableCell>
                <TableCell>Debugging, service reliability</TableCell>
                <TableCell>No</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Precise location, payments data, device IDs</TableCell>
                <TableCell>No</TableCell>
                <TableCell>—</TableCell>
                <TableCell>No</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            We collect browsing data only when you explicitly save a page or enable "Capture Content". We do not collect or sell your general browsing history.
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ mt: 6, display: 'block', color: 'text.disabled' }}>
          This notice summarizes our practices. Product UI may present additional, in-context disclosures and controls. A Data Protection Addendum and Subprocessors list are available at /legal or on request.
        </Typography>
      </Container>
    </>
  );
}